// ✅ src/pages/Inventory/VehicleHsdHourUpdationListing.jsx  (UPDATED FULL FILE)
// Old ASPX: VehicleHsdHourUpdationListing.aspx  (INFRA SCREEN : A-39/1)
//
// Updated pattern aligned with FuelConsumption.jsx:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + searching + exporting states
// - safe init / branch vehicle-code load / search / export handling
// - consistent card / header / button style
// - cleaner reset / validation / navigation handling
//
// Backend expected:
//   app.use("/api/vehicleHsdHrUpdation", vehicleHsdHrUpdation);
//
// APIs used:
//   GET  http://nissi-india.com/api/vehicleHsdHrUpdation/init?branchId=8&branchType=HO
//   GET  http://nissi-india.com/api/vehicleHsdHrUpdation/vehicleCodes?branch=8
//   GET  http://nissi-india.com/api/vehicleHsdHrUpdation/list?branch=8&fromDate=2026-02-01&toDate=2026-02-24&vehicleCode=Select
//   GET  http://nissi-india.com/api/vehicleHsdHrUpdation/export?branch=8&fromDate=...&toDate=...&vehicleCode=...
//
// NOTE:
// - Backend recommended to accept yyyy-mm-dd.
// - If backend/SP requires dd/MM/yyyy, switch params to toDMY(...) where marked.

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaFilter,
  FaArrowLeft,
  FaFileExcel,
  FaInfoCircle,
  FaRedo,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/vehicleHsdHrUpdation";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? Math.trunc(x) : def;
};

const toNum = (v, def = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const pad = (n) => String(n).padStart(2, "0");

const toYYYYMMDD = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = pad(dt.getMonth() + 1);
  const dd = pad(dt.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

const toDMY = (yyyyMmDd) => {
  const s = toStr(yyyyMmDd);
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [yyyy, mm, dd] = s.split("-");
    return `${dd}/${mm}/${yyyy}`;
  }
  return s;
};

const sixMonthsBack = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d;
};

const normalizeOptionList = (arr, fallbackSelect = true) => {
  const list = Array.isArray(arr) ? arr : [];

  const mapped = list.map((x) => {
    if (typeof x === "string") return { label: x, value: x };

    const label =
      x.label ??
      x.fld_branchname ??
      x.fld_vehiclecode ??
      x.fld_vehichlecode ??
      x.value ??
      "";

    const value =
      x.value ??
      x.fld_branchid ??
      x.fld_vehiclecode ??
      x.fld_vehichlecode ??
      x.label ??
      "";

    return {
      label: String(label),
      value: String(value),
    };
  });

  if (!fallbackSelect) return mapped;

  const hasSelect = mapped.some(
    (o) => toStr(o.value).toLowerCase() === "select"
  );

  return hasSelect ? mapped : [{ label: "Select", value: "Select" }, ...mapped];
};

const getCell = (r, keys) => {
  for (const k of keys) {
    const v = r?.[k];
    if (v !== null && v !== undefined && String(v).trim() !== "") return v;
  }
  return "";
};

const VehicleHsdHourUpdationListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdFromStorage =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchTypeFromStorage =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdFromStorage,
      "x-branch": branchTypeFromStorage,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdFromStorage, branchTypeFromStorage, userId]
  );

  const today = useMemo(() => new Date(), []);
  const defaultFromDate = useMemo(() => toYYYYMMDD(sixMonthsBack()), []);
  const defaultToDate = useMemo(() => toYYYYMMDD(today), [today]);

  const userCtx = useMemo(
    () => ({
      branchId: toInt(branchIdFromStorage, 0),
      branchType: clean(branchTypeFromStorage || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [branchIdFromStorage, branchTypeFromStorage, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [dd, setDd] = useState({
    branches: [{ label: "Select", value: "Select" }],
    vehicleCodes: [{ label: "Select", value: "Select" }],
  });

  const [filters, setFilters] = useState({
    branch:
      userCtx.branchType === "HO" ? "Select" : String(userCtx.branchId || ""),
    vehicleCode: "Select",
    fromDate: defaultFromDate,
    toDate: defaultToDate,
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [pageLoading, setPageLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [vehicleCodesLoading, setVehicleCodesLoading] = useState(false);

  const isBusy = pageLoading || searching || exporting || vehicleCodesLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () => `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const valueClass = useMemo(
    () => `text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  const tableHead = useMemo(
    () => [
      "S.No",
      "Date",
      "Issue Qty",
      "Vehicle Code:Name",
      "Pre HSD Starting Hr",
      "Cur HSD Starting Hr",
      "Pre HSD Closing Hr",
      "Cur HSD Closing Hr",
      "Pre HSD Starting KM",
      "Cur HSD Starting KM",
      "Pre HSD Closing KM",
      "Cur HSD Closing KM",
      "Updated Date",
      "User Name",
    ],
    []
  );

  const currentBranchValue = useMemo(() => {
    if (userCtx.branchType !== "HO") return String(userCtx.branchId || "0");
    return isSelect(filters.branch) ? "Select" : String(filters.branch);
  }, [userCtx.branchType, userCtx.branchId, filters.branch]);

  const validateFilters = useCallback(
    (f = filters) => {
      if (userCtx.branchType === "HO" && isSelect(f.branch)) {
        toast.warn("Branch is required");
        return false;
      }

      if (!f.fromDate) {
        toast.warn("From Date is required");
        return false;
      }

      if (!f.toDate) {
        toast.warn("To Date is required");
        return false;
      }

      const fd = new Date(f.fromDate);
      const td = new Date(f.toDate);

      if (Number.isNaN(fd.getTime()) || Number.isNaN(td.getTime())) {
        toast.warn("Invalid date selection");
        return false;
      }

      if (fd > td) {
        toast.warn("From Date cannot be greater than To Date");
        return false;
      }

      return true;
    },
    [filters, userCtx.branchType]
  );

  const resetRows = useCallback(() => {
    setRows([]);
    setTotal(0);
  }, []);

  const syncRowsFromResponse = useCallback((data) => {
    const list = Array.isArray(data?.rows) ? data.rows : [];
    setRows(list);
    setTotal(toInt(data?.total, list.length));
  }, []);

  /* =========================================================
     INIT
     ========================================================= */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: userCtx.branchId,
          branchType: userCtx.branchType,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Init failed");
        return;
      }

      setDd({
        branches: normalizeOptionList(data?.branches),
        vehicleCodes: normalizeOptionList(data?.vehicleCodes),
      });

      setFilters((prev) => ({
        ...prev,
        branch:
          userCtx.branchType === "HO"
            ? prev.branch || "Select"
            : String(userCtx.branchId),
      }));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [userCtx.branchId, userCtx.branchType, commonHeaders]);

  /* =========================================================
     VEHICLE CODE BY BRANCH
     ========================================================= */
  const loadVehicleCodesByBranch = useCallback(
    async (branchValue) => {
      try {
        setVehicleCodesLoading(true);

        const branch =
          userCtx.branchType !== "HO"
            ? String(userCtx.branchId)
            : isSelect(branchValue)
            ? "Select"
            : String(branchValue);

        const { data } = await axios.get(`${API_BASE}/vehicleCodes`, {
          params: { branch },
          headers: commonHeaders,
        });

        if (!data?.success) {
          toast.error(data?.message || "Vehicle codes load failed");
          setDd((prev) => ({
            ...prev,
            vehicleCodes: [{ label: "Select", value: "Select" }],
          }));
          return;
        }

        setDd((prev) => ({
          ...prev,
          vehicleCodes: normalizeOptionList(data?.vehicleCodes),
        }));
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load Vehicle Codes"
        );
        setDd((prev) => ({
          ...prev,
          vehicleCodes: [{ label: "Select", value: "Select" }],
        }));
      } finally {
        setVehicleCodesLoading(false);
      }
    },
    [userCtx.branchType, userCtx.branchId, commonHeaders]
  );

  /* =========================================================
     SEARCH / LIST
     ========================================================= */
  const loadList = useCallback(
    async (override = {}) => {
      const f = { ...filters, ...override };

      const ok = validateFilters(f);
      if (!ok) return;

      try {
        setSearching(true);

        const params = {
          branch:
            userCtx.branchType !== "HO"
              ? String(userCtx.branchId)
              : isSelect(f.branch)
              ? "Select"
              : String(f.branch),
          vehicleCode: isSelect(f.vehicleCode) ? "Select" : String(f.vehicleCode),

          // Recommended: yyyy-mm-dd
          fromDate: f.fromDate,
          toDate: f.toDate,

          // If backend expects dd/MM/yyyy, use below instead:
          // fromDate: toDMY(f.fromDate),
          // toDate: toDMY(f.toDate),
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          toast.error(data?.message || "List failed");
          resetRows();
          return;
        }

        syncRowsFromResponse(data);
        toast.success("Search completed");
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        resetRows();
      } finally {
        setSearching(false);
      }
    },
    [
      filters,
      validateFilters,
      userCtx.branchType,
      userCtx.branchId,
      commonHeaders,
      resetRows,
      syncRowsFromResponse,
    ]
  );

  /* =========================================================
     EXPORT
     ========================================================= */
  const exportExcel = useCallback(async () => {
    const ok = validateFilters();
    if (!ok) return;

    try {
      setExporting(true);

      const params = {
        branch:
          userCtx.branchType !== "HO"
            ? String(userCtx.branchId)
            : isSelect(filters.branch)
            ? "Select"
            : String(filters.branch),
        vehicleCode: isSelect(filters.vehicleCode)
          ? "Select"
          : String(filters.vehicleCode),
        fromDate: filters.fromDate,
        toDate: filters.toDate,

        // If backend expects dd/MM/yyyy:
        // fromDate: toDMY(filters.fromDate),
        // toDate: toDMY(filters.toDate),
      };

      const res = await axios.get(`${API_BASE}/export`, {
        params,
        headers: commonHeaders,
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type:
          res.headers["content-type"] || "application/vnd.ms-excel",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const fileName = `HSD_Hr_Updation_${filters.fromDate}_to_${filters.toDate}.xls`.replace(
        /[:]/g,
        "-"
      );

      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel exported");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Excel export failed");
    } finally {
      setExporting(false);
    }
  }, [
    validateFilters,
    userCtx.branchType,
    userCtx.branchId,
    filters.branch,
    filters.vehicleCode,
    filters.fromDate,
    filters.toDate,
    commonHeaders,
  ]);

  const doReset = useCallback(async () => {
    const reset = {
      branch:
        userCtx.branchType === "HO" ? "Select" : String(userCtx.branchId || ""),
      vehicleCode: "Select",
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    };

    setFilters(reset);
    resetRows();

    await loadVehicleCodesByBranch(reset.branch);

    toast.info("Filters reset to default range (last 6 months)");
  }, [
    userCtx.branchType,
    userCtx.branchId,
    defaultFromDate,
    defaultToDate,
    resetRows,
    loadVehicleCodesByBranch,
  ]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onBranchChange = useCallback(
    async (val) => {
      setFilters((prev) => ({
        ...prev,
        branch: val,
        vehicleCode: "Select",
      }));
      await loadVehicleCodesByBranch(val);
    },
    [loadVehicleCodesByBranch]
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (userCtx.branchType !== "HO" && userCtx.branchId) {
      loadVehicleCodesByBranch(String(userCtx.branchId));
    }
  }, [userCtx.branchType, userCtx.branchId, loadVehicleCodesByBranch]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              HSD HR UPDATION LISTING
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                HSD Hr Updation Listing
              </h1>

              <span className={pillClass}>INFRA SCREEN : A-39/1</span>

              <span className={pillClass}>{total} Records</span>

              <span className={labelClass}>
                Branch: <span className={valueClass}>{currentBranchValue || "-"}</span>
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {isBusy && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : vehicleCodesLoading
                    ? "Loading vehicle codes..."
                    : searching
                    ? "Searching..."
                    : exporting
                    ? "Exporting..."
                    : "Processing..."}
                </span>
              )}

              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${showFilters ? (isDark ? "bg-gray-800" : "bg-gray-50") : ""} ${
                  isBusy ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <FaFilter />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate(-1)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 uppercase ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter />
                Filtration Area
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={(e) => onBranchChange(e.target.value)}
                    className={inputClass}
                    disabled={userCtx.branchType !== "HO" || isBusy}
                  >
                    {dd.branches.map((b) => (
                      <option key={String(b.value)} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <select
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {dd.vehicleCodes.map((v) => (
                      <option key={String(v.value)} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:justify-end md:items-center gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={doReset}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg border ${
                      isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                    title="Reset to default range"
                  >
                    <FaRedo />
                    Reset
                  </button>

                  <button
                    type="button"
                    onClick={() => loadList()}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {searching ? "Searching..." : "Search"}
                  </button>

                  <button
                    type="button"
                    onClick={exportExcel}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaFileExcel />
                    {exporting ? "Exporting..." : "Excel"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records Found
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Select filters and click “Search”.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      {tableHead.map((h) => (
                        <th
                          key={h}
                          className="px-4 py-4 text-center font-semibold whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${
                          isDark ? "bg-gray-800" : "bg-white"
                        } hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors duration-200`}
                      >
                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {idx + 1}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_SIdate", "fld_SIDate", "Date"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_qty", "fld_Qty", "Qty"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, [
                            "fld_vehiclecode",
                            "VehicleCode",
                            "fld_vehiclecodename",
                          ])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_Pre_HSDStartingHr"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_Cur_HSDStartingHr"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_Pre_HSDClosingHr"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_Cur_HSDClosingHr"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_Pre_HSDStartingKM"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_Cur_HSDStartingKM"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_Pre_HSDClosingKM"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_Cur_HSDClosingKM"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_date", "fld_Date", "UpdatedDate"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCell(r, ["fld_username", "UserName"])}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleHsdHourUpdationListing;